import { AxiosError } from 'axios'
import { DataTableController } from 'components/common'
import ActiveFilterChip from 'components/common/activeFilterChip'
import BarcodeSearch from 'components/common/barcodeSearch'
import {
  BARCODE_COUNT_KEY,
  BARCODE_ERROR_MESSAGES,
  BARCODE_QUERY_KEY,
  BARCODE_SEARCH_EXPIRED,
  BARCODE_TOKEN_KEY,
  getBarcodeErrorCode,
} from 'components/common/barcodeSearch/constants'
import DataTableImportButton from 'components/common/dataTable/dataTableImportButton'
import { ImportColumn } from 'components/common/importModal'
import {
  GenerateInvalideOnlinePrice,
  GenerateUpdatedErpPriceReport,
  PricingDataFilterForm,
  PricingDataTable,
  columns,
} from 'components/pricing'
import { FILTER_FIELD_PRICING } from 'components/pricing/pricingFilterForm'
import { usePageAccess, useQuery } from 'hooks'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import toast from 'react-hot-toast'
import {
  exportPricingData,
  importPricingData,
  importOnlinePricingData,
  importAggregatorWiseOnlinePricingData,
  uploadPricingBarcodeSearchFile,
} from 'utils/api/pricingApi'
import { filterEmptyValues } from 'utils/helpers'
import { downloadImportSample, getImportTemplate } from 'utils/api/importApi'

const Pricing = () => {
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])
  const [onlinePriceColumns, setOnlinePriceColumns] = useState<ImportColumn[]>(
    [],
  )
  const [aggOnlinePriceColumns, setAggOnlinePriceColumns] = useState<
    ImportColumn[]
  >([])

  const { isEditor } = usePageAccess()

  const { getQuery, setMultipleQueries } = useQuery()

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  // Will handle the file export funtionality with toast
  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_PRICING)
    const barcodeToken = getQuery(BARCODE_TOKEN_KEY)
    const barcode = getQuery(BARCODE_QUERY_KEY)

    // filtering empty values and keys of filter
    const pricingFilter = filters ? filterEmptyValues(filters) : {}

    // A barcode search on its own is enough to scope the export; otherwise at
    // least one filter is required.
    if (!barcodeToken && !barcode && Object.keys(pricingFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await exportPricingData({ filters: pricingFilter, barcodeToken, barcode })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      // Token expired mid-flight → clear the search and prompt a re-upload.
      if (getBarcodeErrorCode(error) === BARCODE_SEARCH_EXPIRED) {
        setMultipleQueries({
          [BARCODE_TOKEN_KEY]: undefined,
          [BARCODE_COUNT_KEY]: undefined,
          page: 1,
        })
        return toast.error(BARCODE_ERROR_MESSAGES[BARCODE_SEARCH_EXPIRED], {
          id: loadingToast,
          duration: 4000,
        })
      }
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }

      toast.error('Failed to export Pricing Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  useEffect(() => {
    const selectedColumnState = localStorage.getItem('pricingSelectedColumns')

    if (selectedColumnState) {
      setMultipleQueries({ cols: JSON.parse(selectedColumnState) })
    }
  }, [])

  useEffect(() => {
    getImportTemplate('pricing')
      .then((res) => setImportColumns(res.data.columns))
      .catch(() => {})
  }, [])

  useEffect(() => {
    getImportTemplate('onlinePrice')
      .then((res) => setOnlinePriceColumns(res.data.columns))
      .catch(() => {})
  }, [])

  useEffect(() => {
    getImportTemplate('aggregatorOnlinePrice')
      .then((res) => setAggOnlinePriceColumns(res.data.columns))
      .catch(() => {})
  }, [])

  const handleDownloadSample = async () => {
    const response = await downloadImportSample('pricing')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'pricing_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadOnlinePriceSample = async () => {
    const response = await downloadImportSample('onlinePrice')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'online_price_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadAggOnlinePriceSample = async () => {
    const response = await downloadImportSample('aggregatorOnlinePrice')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'aggregator_online_price_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Pricing</title>
      </Helmet>

      <div className="grid grid-cols-2">
        <GenerateInvalideOnlinePrice />
        <GenerateUpdatedErpPriceReport />
        <div className="flex grow items-center">
          {isEditor && importOnlinePricingData && (
            <DataTableImportButton
              label="Import Online Price"
              disabled={loading}
              uploadApiFunction={importOnlinePricingData}
              className="mb-5 border border-gray-300"
              importColumns={onlinePriceColumns}
              importModuleName="online price"
              onDownloadSample={handleDownloadOnlinePriceSample}
            />
          )}
          {isEditor && importAggregatorWiseOnlinePricingData && (
            <DataTableImportButton
              label="Import Aggregator Wise Online Price"
              disabled={loading}
              uploadApiFunction={importAggregatorWiseOnlinePricingData}
              className="mb-5 ml-3 border border-gray-300"
              importColumns={aggOnlinePriceColumns}
              importModuleName="aggregator wise online price"
              onDownloadSample={handleDownloadAggOnlinePriceSample}
            />
          )}
        </div>
      </div>

      <DataTableController
        isEditor={isEditor}
        columns={columns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleImport={importPricingData}
        handleExport={handleExport}
        activeColumnKey="pricing"
        placeholder="Barcode, Store code and Aggregator code"
        defaultSelectedColumns={selectedColumns}
        loading={loading}
        importColumns={importColumns}
        importModuleName="pricing"
        onDownloadSample={handleDownloadSample}
      />

      <BarcodeSearch uploadFile={uploadPricingBarcodeSearchFile} />

      {!showFilter && <ActiveFilterChip filterKey={FILTER_FIELD_PRICING} />}

      {showFilter ? (
        <PricingDataFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <PricingDataTable
          isEditor={isEditor}
          setSelectedColumns={setSelectedColumns}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default Pricing
